import React from 'react'
import cx from 'classnames'
import { useNavigate } from 'react-router-dom'
import { noOp } from '../../../../shared/helpers.js'
import googleAnalytics4 from '../../lib/googleAnalytics.js'
import tts from '../../lib/TTS.js'
import { useHybridFocusable } from '../../hooks/useHybridFocusable'
import { useEvergreenMessaging } from './EvergreenMessagingProvider'

import type { EvergreenUrgentBannerProps } from './types'

function EvergreenUrgentBanner({
  className = '',
  onFocus = noOp,
  onBlur = noOp,
}: EvergreenUrgentBannerProps) {
  const navigate = useNavigate()
  const { messagingConfig } = useEvergreenMessaging()

  const handleFocus = (...args: Parameters<NonNullable<typeof onFocus>>) => {
    tts.stopRead()
    if (messagingConfig) {
      tts.addTextToRead(
        `${messagingConfig.headerText}. ${messagingConfig.ctaText}`
      )
    }
    onFocus(...args)
  }

  const handleEnterPress = () => {
    if (messagingConfig) {
      googleAnalytics4.sendCustomEvent('feature_tracking', {
        feature_category: 'urgent messaging',
        feature_name: 'national messaging',
        object_name: 'national messaging - home banner',
        object_type: 'banner',
        object_position: '2',
        object_location: 'content row',
        object_action: 'button press',
        object_action_behavior: 'next screen',
        campaign_name: messagingConfig.campaign,
      })
    }
    navigate('/evergreen-more-info')
  }

  const { ref, focused, isHovered } = useHybridFocusable({
    onEnterPress: handleEnterPress,
    onFocus: handleFocus,
    onBlur,
  })

  if (!messagingConfig) {
    return null
  }

  const {
    bannerBackgroundColor,
    bannerTextColor,
    headerText,
    ctaImageUrl,
    ctaText,
  } = messagingConfig

  return (
    <div
      ref={ref}
      className={cx('evergreen-urgent-banner', className, {
        'evergreen-urgent-banner--focused': focused,
        'evergreen-urgent-banner--hovered': isHovered,
      })}
      style={{ backgroundColor: bannerBackgroundColor }}
    >
      <div className="evergreen-urgent-banner__content">
        <span
          className="evergreen-urgent-banner__header"
          style={{ color: bannerTextColor }}
        >
          {headerText}
        </span>
        {ctaImageUrl && (
          <img
            className="evergreen-urgent-banner__cta-image"
            src={ctaImageUrl}
            alt=""
          />
        )}
        <span
          className="evergreen-urgent-banner__cta-text"
          style={{ color: bannerTextColor }}
        >
          {ctaText}
        </span>
      </div>
    </div>
  )
}

export default EvergreenUrgentBanner
